import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Header,
    Button,
    Message,
    Icon,
} from 'semantic-ui-react';
import { LoadingWrapper, ReactTable } from '../../common';
import { useRoadblockDashboard } from './hooks';
import { ResolveRoadblockModal } from './ResolveRoadblockModal';
import { ExportModal } from './ExportModal';
import { formatDateToMmDdYyyy } from '../../../utils/formatDate';
import { roadblockTypePillStyle } from './constants';
import { BooleanColumnFilter, BooleanFilterType } from '../../common';
import { useAuth } from '../../auth';

const RoadblockTypeCell = ({ value, label }) => (
    <span style={roadblockTypePillStyle(value)}>
        {label || value}
    </span>
);

export const RoadblockDashboard = () => {
    const navigate = useNavigate();
    const { isReadOnly } = useAuth();
    const {
        loaded,
        error,
        successMessage,
        setSuccessMessage,
        resolvingId,
        setResolvingId,
        showExportModal,
        setShowExportModal,
        roadblocks,
        dfnOptions,
        fetchRoadblocks,
    } = useRoadblockDashboard();

    const columns = useMemo(
        () => [
            {
                Header: 'ID',
                accessor: 'id',
                Cell: ({ value }) => (
                    <span
                        style={{ color: '#2185d0', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => navigate(`/forms/roadblock/${value}`)}
                    >
                        #{value}
                    </span>
                ),
            },
            {
                Header: 'Team',
                accessor: 'teamName',
            },
            {
                Header: 'Assigned To',
                accessor: 'assignedToName',
                Cell: ({ value }) => value || '—',
            },
            {
                Header: 'Roadblock Type',
                accessor: 'roadblockType',
                Cell: ({ row }) => (
                    <RoadblockTypeCell
                        value={row.original.roadblockType}
                        label={row.original.roadblockTypeDisplay}
                    />
                ),
            },
            {
                Header: 'Subtype',
                accessor: 'roadblockSubtypeLabel',
                Cell: ({ value }) => value || '—',
            },
            {
                Header: 'Date Opened',
                accessor: 'date',
                Cell: ({ value }) => formatDateToMmDdYyyy(value),
            },
            {
                Header: 'Production Impact',
                accessor: 'productionImpact',
                Cell: ({ value }) => (value ? 'Yes' : 'No'),
                Filter: row => BooleanColumnFilter(row, 'Yes', 'No'),
                filter: BooleanFilterType,
            },
            {
                Header: 'DFN',
                accessor: 'dfn',
                Cell: ({ value }) => dfnOptions.find(o => o.value === value)?.text || value || '—',
            },
            {
                Header: 'Actions',
                id: 'actions',
                disableSortBy: true,
                Cell: ({ row }) => (
                    <Button
                        basic
                        size="mini"
                        color="blue"
                        onClick={(e) => {
                            e.stopPropagation();
                            setResolvingId(row.original.id);
                        }}
                    >
                        Resolve
                    </Button>
                ),
            },
        ],
        [navigate, setResolvingId, dfnOptions]
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', paddingTop: '1rem' }}>

            <div style={{ width: '95%', textAlign: 'center', marginBottom: '0.75rem' }}>
                <Header as="h2" style={{ marginBottom: '0.25rem' }}>Roadblocks</Header>
                <p style={{ color: '#666', margin: 0 }}>
                    View and manage open roadblocks across all teams.
                </p>
            </div>

            <div style={{ width: '95%', display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'center', marginBottom: '0.75rem' }}>
                <Button basic onClick={() => setShowExportModal(true)}>
                    <Icon name="download" />
                    Export Data
                </Button>
                {!isReadOnly && (
                    <Button primary onClick={() => navigate('/forms/roadblock/new')}>
                        <Icon name="plus" />
                        New Roadblock Form
                    </Button>
                )}
            </div>

            {error && <Message negative style={{ width: '95%' }}>{error}</Message>}
            {successMessage && (
                <Message positive style={{ width: '95%' }} onDismiss={() => setSuccessMessage('')}>
                    {successMessage}
                </Message>
            )}

            <div style={{ width: '100%' }}>
                <LoadingWrapper loading={!loaded}>
                    {roadblocks.length === 0 && loaded ? (
                        <Message info style={{ width: '95%' }}>No open roadblocks found.</Message>
                    ) : (
                        <ReactTable
                            className="channel-table"
                            data={roadblocks}
                            columns={columns}
                            getRowProps={(row) => ({
                                onClick: () => navigate(`/forms/roadblock/${row.original.id}`),
                                style: { cursor: 'pointer' },
                            })}
                        />
                    )}
                </LoadingWrapper>
            </div>

            <ResolveRoadblockModal
                open={resolvingId !== null}
                roadblockId={resolvingId}
                onClose={() => setResolvingId(null)}
                onResolved={() => {
                    setResolvingId(null);
                    fetchRoadblocks();
                    setSuccessMessage('Roadblock resolved successfully.');
                }}
            />

            <ExportModal
                open={showExportModal}
                onClose={() => setShowExportModal(false)}
                onExportSuccess={() => setSuccessMessage('Export started — check your downloads.')}
            />
        </div>
    );
};
